import * as Service from 'types/service';
import { ClickAwayListener, Popper } from '@material-ui/core';
import {
  EventAction,
  EventAttributesActionLocation,
  EventCustomAttributes,
} from 'utils/analytics/amplitudeEventConstants';
import { PrintChitSource } from 'constants/PrinterConstants';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { ViewMode } from 'routes/authenticated/app/bookings/CalendarAndServiceRoute';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { css } from '@emotion/react';
import { preventBubbleOutside } from '@tock/shared/utils/eventUtils';
import { secondaryStatesSelector } from 'selectors/service/secondaryStatesSelector';
import { useAsyncRender } from 'contexts/asyncRender';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import PartyPopoverLegacy from 'components/service/party/partyPopover/PartyPopoverLegacy';
import PartyPopoverRefresh from 'components/service/party/partyCardRefresh/PartyPopoverRefresh';

interface Props {
  anchorEle?: HTMLDivElement;
  isOpen: boolean;
  isReadOnly: boolean;
  party: Service.Party;
  selectedParty?: Service.Party;
  viewMode: ViewMode;
  onCancelPurchase(party?: Service.Party): void;
  onClose(): void;
  onEditReservation(actionLocation?: EventAttributesActionLocation): void;
  onPrintChit(party: Service.Party | undefined, source: PrintChitSource): void;
  onFinishSeatedPartiesAtTablesAndSeatNewParty(tableIds: number[], party: Service.Party): void;
  onSelectParty(party: Service.Party): void;
}

/* Note: handleSelectParty/onSelectParty is how parent (CalendarAndServiceRoute) sets `selectedCalendarItem`.
 * The selectedCalendarItem is what the callbacks like onCancelPurchase/onEditReservation execute on.
 * However handleSelectParty/onSelectParty triggers the reservationsSidePanel to open.
 * Some "would be nice" future work would be to set selectedCalendarItem when this component mounts and
 * refactor CalendarAndServiceRoute a little as to avoid
 * ```
 *  handleSelectParty(party);
 *  actions.reservationsSidePanel.closeSidePanel();
 * ```
 */
const PartyPopoverFC: React.FC<React.PropsWithChildren<Props>> = ({
  anchorEle,
  isOpen,
  isReadOnly,
  party,
  selectedParty,
  viewMode,
  onCancelPurchase,
  onClose,
  onEditReservation,
  onPrintChit,
  onFinishSeatedPartiesAtTablesAndSeatNewParty,
  onSelectParty,
}) => {
  const { confirm } = useAsyncRender();
  const isPartyStatusRefreshEnabled = useFeatureFlag('PARTY_STATUS_REFRESH');
  const popoverWrapperRef = useRef<HTMLDivElement>(null);
  const { actions } = useActions();
  const secondaryStates = useSelector(secondaryStatesSelector);
  const printOnSeat =
    useSelector((state: StoreType) => state.business.data?.printChitWhenSeating) ?? false;

  const handleSave = (party: Service.Party, state: string, secondaryState: string | null) => {
    actions.elmParties.updatePartyState(
      party,
      state,
      secondaryState,
      [],
      EventAttributesActionLocation.PARTY_POPOVER
    );
    onClose();
  };

  const handleSelectParty = (party: Service.Party) => {
    if (!selectedParty || selectedParty.id !== party.id) {
      onSelectParty(party);
    }
  };

  const handleRemoveWalkIn = async () => {
    try {
      await confirm('Removing a walk-in cannot be reversed', {
        cancelText: 'No',
        continueText: 'Yes',
        isDestructive: true,
      });
    } catch {
      return;
    }

    actions.serviceUiPartyDisplay.deselectParty();
    actions.elmParties.removeWalkInParty(party);
    onClose();
  };

  const handleClickViewProfile = (): void => {
    handleSelectParty(party);
    actions.reservationsSidePanel.closeSidePanel();
    actions.reservationsSidePanel.openPartyFullPanel();
    onClose();
  };

  const handleClickAway = (event: React.MouseEvent<Document, MouseEvent>) => {
    const el = popoverWrapperRef?.current;
    if (!el || el.contains((event?.target as Node) || null)) {
      return;
    }

    onClose();
  };

  return (
    <Popper
      id={'PartyPopover'}
      open={isOpen}
      anchorEl={anchorEle}
      placement="right-start"
      css={css`
        z-index: 1300;
      `}
      onClick={preventBubbleOutside}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <div
          ref={popoverWrapperRef}
          css={css`
            background-color: var(--bg-default);
            border: 1px solid var(--border-default);
            border-radius: 0.6rem;
            box-shadow: var(--box-shadow-primary);
            padding: 1.2rem;
            width: ${isPartyStatusRefreshEnabled ? '30rem' : '24rem'};
          `}
          onClick={preventBubbleOutside}
        >
          {isPartyStatusRefreshEnabled ? (
            <PartyPopoverRefresh
              isReadOnly={isReadOnly}
              party={party}
              viewMode={viewMode}
              onCancelPurchase={onCancelPurchase}
              onClose={onClose}
              onEditReservation={onEditReservation}
              onOpenParty={handleClickViewProfile}
              onRemoveWalkIn={handleRemoveWalkIn}
              onSelectParty={handleSelectParty}
            />
          ) : (
            <PartyPopoverLegacy
              isReadOnly={isReadOnly}
              party={party}
              printOnSeat={printOnSeat}
              secondaryStates={secondaryStates}
              viewMode={viewMode}
              onCancelPurchase={onCancelPurchase}
              onClose={onClose}
              onEditReservation={onEditReservation}
              onFinishSeatedPartiesAtTablesAndSeatNewParty={
                onFinishSeatedPartiesAtTablesAndSeatNewParty
              }
              onOpenParty={handleClickViewProfile}
              onPrintChit={onPrintChit}
              onRemoveWalkIn={handleRemoveWalkIn}
              onSelectParty={handleSelectParty}
              onUpdatePartyState={handleSave}
            />
          )}
        </div>
      </ClickAwayListener>
    </Popper>
  );
};

export function trackPartyEventUtility(
  action: EventAction,
  { type }: Service.Party,
  tableAssignmentStartedDetails?: EventCustomAttributes['tableAssignmentStartedDetails']
) {
  amplitudeSendEvent(action, {
    actionLocation: EventAttributesActionLocation.PARTY_PANEL,
    partyType: type.toLowerCase() as Lowercase<Service.Party['type']>,
    tableAssignmentStartedDetails,
  });
}

export default PartyPopoverFC;
